import PlacesAutocomplete from "stimulus-places-autocomplete";
import parseGooglePlace from "parse-google-place";

window.initPlacesAutocomplete = function () {
  const event = new Event("initPlacesAutocomplete", {
    bubbles: true,
    cancelable: true,
  });
  window.dispatchEvent(event);
};

interface Address {
  address1: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
const elementLocator = (
  prop: any,
  selector: string,
  maxSearchLevel = 12,
  level = 0
): HTMLElement | void => {
  level++;

  if (
    level === maxSearchLevel + 1 ||
    !prop ||
    !(Array.isArray(prop) || prop === Object(prop))
  ) {
    return;
  }

  if (
    prop === Object(prop) &&
    prop instanceof HTMLElement &&
    prop.matches(selector)
  ) {
    return prop as HTMLElement;
  }

  for (const key in prop) {
    if (Object.prototype.hasOwnProperty.call(prop, key)) {
      const element = elementLocator(
        prop[key],
        selector,
        maxSearchLevel,
        level
      );
      if (element) {
        return element;
      }
    }
  }
};

export default class extends PlacesAutocomplete {
  static targets = [
    "address1",
    "address2",
    "city",
    "state",
    "postcode",
    "country",
  ];

  declare readonly address1Target: HTMLInputElement;
  declare readonly address2Target: HTMLInputElement;
  declare readonly cityTarget: HTMLInputElement;
  declare readonly stateTarget: HTMLInputElement;
  declare readonly postcodeTarget: HTMLInputElement;
  declare readonly countryTarget: HTMLInputElement;
  declare readonly hasAddress1Target: boolean;
  declare readonly hasAddress2Target: boolean;
  declare readonly hasCityTarget: boolean;
  declare readonly hasStateTarget: boolean;
  declare readonly hasPostcodeTarget: boolean;
  declare readonly hasCountryTarget: boolean;

  connect() {
    if (typeof google !== "undefined") {
      this.initAutocomplete();
    }
  }

  disconnect() {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      google.maps.event.trigger(this.address1Target, "blur");
      google.maps.event.clearInstanceListeners(this.address1Target);
      const element = elementLocator(this.autocomplete, ".pac-container");
      if (element) {
        element.remove();
      }
      // @ts-expect-error
      delete this.autocomplete;
    }
  }

  initAutocomplete() {
    if (this.autocomplete) return;

    this.autocomplete = new google.maps.places.Autocomplete(
      this.address1Target,
      this.autocompleteOptions
    );
    this.autocomplete.addListener("place_changed", this.placeChanged);
  }

  placeChanged(): void {
    const place = this.autocomplete.getPlace();
    const address = this.parsePlace(place);
    this.setAddressComponents(address);
    const event = new Event("placeChange", {
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  }

  setAddressComponents(address: Address): void {
    if (this.hasAddress1Target) this.address1Target.value = address.address1;
    if (this.hasAddress2Target) this.address2Target.value = "";
    if (this.hasCityTarget) this.cityTarget.value = address.city;
    if (this.hasStateTarget) this.stateTarget.value = address.state;
    if (this.hasPostcodeTarget) this.postcodeTarget.value = address.postcode;
    if (this.hasCountryTarget) this.countryTarget.value = address.country;
  }

  private parsePlace(place: google.maps.places.PlaceResult): Address {
    const addressParts = parseGooglePlace(place);

    let address1 = addressParts.address;
    let city = addressParts.city;

    if (place.adr_address) {
      const microformat = new DOMParser().parseFromString(
        place.adr_address,
        "text/html"
      );
      const streetAddress =
        microformat.querySelector(".street-address")?.innerHTML;
      if (typeof streetAddress !== "undefined") {
        address1 = streetAddress;
      }
      const locality = microformat.querySelector(".locality")?.innerHTML;
      if (typeof locality !== "undefined") {
        city = locality;
      }
    }

    return {
      address1,
      city,
      state: addressParts.stateShort,
      postcode: addressParts.zipCode,
      country: addressParts.countryShort,
    };
  }

  get autocompleteOptions(): google.maps.places.AutocompleteOptions {
    return {
      fields: ["address_components", "adr_address"],
    };
  }
}
